import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Search, ArrowUpDown, Library, ChevronRight, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import AskParentsDialog from './AskParentsDialog';

const COVERAGE_LABEL = {
  not_covered: 'Not Covered',
  invite_to_mentor: 'Invite to Mentor',
  not_approved: 'Not Approved',
  mentors_no_availability: 'No published availability',
  single_mentor: 'One mentor only',
  covered: 'Covered',
  not_in_library: 'Not in the library',
};

const COVERAGE_TONE = {
  not_covered: 'text-[#B45309]',
  invite_to_mentor: 'text-[#1D4ED8]',
  not_approved: 'text-[#B45309]',
  mentors_no_availability: 'text-[#B45309]',
  single_mentor: 'text-[#6B7280]',
  covered: 'text-[#15803D]',
  not_in_library: 'text-[#6B7280]',
};

const UNCOVERED_STATES = ['not_covered', 'invite_to_mentor', 'not_approved'];

const LIBRARY_STATUS = {
  new: 'Pending review',
  reviewed: 'Under review',
  approved: 'Approved',
  rejected: 'Rejected',
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'needs_coverage', label: 'Needs Coverage' },
  { id: 'invite_to_mentor', label: 'Invite to Mentor' },
  { id: 'no_availability', label: 'No Availability' },
  { id: 'one_mentor', label: 'Only One Mentor' },
  { id: 'not_in_library', label: 'Not in Library' },
];

const COLUMNS = [
  { key: 'career_name', label: 'Career', align: 'left' },
  { key: 'mentor_count', label: 'Mentors', align: 'right' },
  { key: 'request_count', label: 'Requests', align: 'right' },
  { key: 'completed_count', label: 'Completed', align: 'right' },
  { key: 'coverage_state', label: 'Coverage', align: 'left' },
];

const dayLabel = (iso) => (iso
  ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  : '');

const matchesFilter = (row, filter) => {
  if (filter === 'all') return true;
  if (filter === 'not_in_library') return row.kind === 'profession';
  if (row.kind === 'profession') return false;
  if (filter === 'needs_coverage') return UNCOVERED_STATES.includes(row.coverage_state) && row.request_count > 0;
  if (filter === 'invite_to_mentor') return row.coverage_state === 'invite_to_mentor';
  if (filter === 'no_availability') return row.coverage_state === 'mentors_no_availability';
  if (filter === 'one_mentor') return row.coverage_state === 'single_mentor';
  return true;
};

const slugify = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
const rowKey = (r) => (r.kind === 'profession' ? `profession-${slugify(r.profession_label)}` : r.career_slug);

function CoverageDetails({ row, onInvite, onNudge, nudging, refreshToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    void refreshToken;
    setLoading(true);
    try {
      const query = row.kind === 'profession'
        ? `profession_label=${encodeURIComponent(row.profession_label)}`
        : `career_slug=${encodeURIComponent(row.career_slug)}`;
      const r = await api.get(`/coordinator/career-coverage/details?${query}`);
      setData(r.data);
    } catch {
      setData(null);
    }
    setLoading(false);
  }, [row, refreshToken]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="py-6 text-center"><Loader2 className="h-4 w-4 animate-spin text-[#9CA3AF] mx-auto" /></div>
    );
  }
  if (!data) return <p className="text-xs text-[#9CA3AF] py-4">Could not load the details.</p>;

  const invitable = (data.parents || []).filter(p => !p.already_mentoring && !p.requested_this && !p.invited_at);

  return (
    <div className={`grid gap-5 py-4 ${row.kind === 'profession' ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}
      data-testid={`coverage-details-${rowKey(row)}`}>
      {row.kind === 'career' && (
        <div>
          <h4 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-1">Mentors & availability</h4>
          {data.mentors.length === 0 ? (
            <p className="text-xs text-[#9CA3AF]">No mentor covers this career yet.</p>
          ) : data.mentors.map(m => (
            <div key={m.email} className="mb-2.5">
              <p className="text-sm text-[#1F2937]">
                {m.name}
                {m.approval_status !== 'approved' && (
                  <span className="text-[#9CA3AF]"> · {m.approval_status}</span>
                )}
              </p>
              <p className="text-[11px] text-[#6B7280]">
                {m.online_slots.length > 0 ? m.online_slots.join(' · ') : 'No online availability'}
              </p>
              {m.visit_dates.length > 0 && (
                <p className="text-[11px] text-[#6B7280]">
                  Workplace visits: {m.visit_dates.map(dayLabel).join(', ')}
                </p>
              )}
            </div>
          ))}
          {row.coverage_state === 'mentors_no_availability' && (
            row.nudged_at ? (
              <p className="text-[11px] text-[#9CA3AF] mt-1" data-testid={`coverage-nudged-${row.career_slug}`}>
                Nudged on {dayLabel(row.nudged_at)}
              </p>
            ) : (
              <Button variant="outline" size="sm" onClick={() => onNudge(row.career_slug)}
                disabled={nudging === row.career_slug}
                className="mt-1 h-7 rounded-lg text-[11px] border-[#E5E7EB] text-[#4B5563]"
                data-testid={`coverage-nudge-${row.career_slug}`}>
                {nudging === row.career_slug ? 'Nudging…' : 'Nudge the mentor'}
              </Button>
            )
          )}
        </div>
      )}

      <div>
        <h4 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-1">Parent professions</h4>
        {data.parents.length === 0 ? (
          <p className="text-xs text-[#9CA3AF]">No parent at the school reports this work.</p>
        ) : data.parents.map(p => (
          <p key={p.parent_email} className="text-sm text-[#1F2937] mb-1.5">
            {p.parent_name}
            <span className="block text-[11px] text-[#6B7280]">
              {p.profession}
              {p.already_mentoring && ' · already mentors this'}
              {!p.already_mentoring && p.requested_this && ' · asked for this career'}
              {!p.already_mentoring && !p.requested_this && p.invited_at && ` · invited ${dayLabel(p.invited_at)}`}
            </span>
          </p>
        ))}
        {invitable.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => onInvite(row)}
            className="mt-1 h-7 rounded-lg text-[11px] border-[#E5E7EB] text-[#4B5563]"
            data-testid={`coverage-invite-${rowKey(row)}`}>
            Invite {invitable.length} parent{invitable.length === 1 ? '' : 's'}
          </Button>
        )}
      </div>

      <div>
        <h4 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-1">Invitations</h4>
        {data.invitations.length === 0 ? (
          <p className="text-xs text-[#9CA3AF]">Nobody has been invited yet.</p>
        ) : data.invitations.map(i => (
          <p key={i.parent_email} className="text-sm text-[#1F2937] mb-1.5">
            {i.parent_name}
            <span className="block text-[11px] text-[#6B7280]">
              {i.status === 'accepted' ? 'Said yes' : 'Invited'} {dayLabel(i.created_at)}
            </span>
          </p>
        ))}
        {row.kind === 'profession' && (
          <>
            <h4 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mt-4 mb-2">Library review</h4>
            <p className="text-sm text-[#1F2937]" data-testid={`coverage-library-status-${rowKey(row)}`}>
              {LIBRARY_STATUS[row.library_status] || 'Pending review'}
              <span className="block text-[11px] text-[#6B7280]">
                Sent to the CareersPOV team automatically. Nothing for you to do here.
              </span>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default function CareerCoverage({ initialGap }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('in_play');
  const [filter, setFilter] = useState(
    { no_cover: 'needs_coverage', no_times: 'no_availability', single_mentor: 'one_mentor' }[initialGap] || 'all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'request_count', dir: -1 });
  const [expanded, setExpanded] = useState('');
  const [askTarget, setAskTarget] = useState(null);
  const [nudging, setNudging] = useState('');
  const [refreshToken, setRefreshToken] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/coordinator/career-coverage?scope=${scope}`);
      setData(r.data);
    } catch {
      setData(null);
    }
    setLoading(false);
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const nudge = async (slug) => {
    setNudging(slug);
    try {
      const r = await api.post('/coordinator/coverage-nudge', { career_slug: slug });
      toast.success(r.data.message);
      await load();
      setRefreshToken(n => n + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the nudge');
    }
    setNudging('');
  };

  const rows = useMemo(() => {
    let list = (data?.rows || []).filter(r => matchesFilter(r, filter));
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r => r.career_name.toLowerCase().includes(q) || (r.category || '').toLowerCase().includes(q));
    }
    const val = (r) => {
      if (sort.key === 'career_name') return r.career_name.toLowerCase();
      if (sort.key === 'coverage_state') return COVERAGE_LABEL[r.coverage_state] || '';
      return r[sort.key];
    };
    return [...list].sort((a, b) => {
      if ((a.kind === 'profession') !== (b.kind === 'profession')) return a.kind === 'profession' ? 1 : -1;
      const av = val(a), bv = val(b);
      if (av === bv) return a.career_name.localeCompare(b.career_name);
      return av > bv ? sort.dir : -sort.dir;
    });
  }, [data, filter, search, sort]);

  const counts = useMemo(() => {
    const all = data?.rows || [];
    return FILTERS.reduce((acc, f) => ({ ...acc, [f.id]: all.filter(r => matchesFilter(r, f.id)).length }), {});
  }, [data]);

  const toggleSort = (key) => {
    setSort(s => s.key === key ? { key, dir: -s.dir } : { key, dir: ['career_name', 'coverage_state'].includes(key) ? 1 : -1 });
  };

  return (
    <div className="space-y-4" data-testid="career-coverage">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-[#6B7280]">
          What your mentors cover, what families are asking for, and where the gaps are.
        </p>
        <button onClick={() => setScope(s => (s === 'all' ? 'in_play' : 'all'))}
          className="text-xs text-[#F97D6A] hover:text-[#F06651] font-medium flex items-center gap-1.5"
          data-testid="coverage-scope-toggle">
          <Library className="h-3.5 w-3.5" />
          {scope === 'all'
            ? 'Show careers in play only'
            : data?.library_total
              ? `Show all ${data.library_total} careers in the library`
              : 'Show the whole career library'}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => { setFilter(f.id); setExpanded(''); }}
            className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${
              filter === f.id
                ? 'bg-[#1F2937] text-white border-[#1F2937]'
                : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#C8CDD6]'}`}
            data-testid={`coverage-filter-${f.id}`}>
            {f.label} <span className="opacity-60">{counts[f.id] ?? 0}</span>
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9CA3AF]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search careers"
            className="pl-9 h-9 w-56 text-sm rounded-lg border-[#E5E7EB]" data-testid="coverage-search" />
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#9CA3AF] mx-auto" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[#9CA3AF] py-12 text-center bg-white rounded-xl border border-[#E5E7EB]"
          data-testid="coverage-empty">
          Nothing to show here yet.
        </p>
      ) : (
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E7EB]">
                <th className="w-8" />
                {COLUMNS.map(c => (
                  <th key={c.key}
                    className={`px-4 py-2.5 text-${c.align} text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider whitespace-nowrap`}>
                    <button onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1 hover:text-[#1F2937]"
                      data-testid={`coverage-sort-${c.key}`}>
                      {c.label}
                      <ArrowUpDown className={`h-3 w-3 ${sort.key === c.key ? 'text-[#F97D6A]' : 'text-[#D1D5DB]'}`} />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const key = rowKey(r);
                const open = expanded === key;
                return (
                  <React.Fragment key={key}>
                    <tr onClick={() => setExpanded(open ? '' : key)}
                      className="border-b border-[#F3F4F6] last:border-0 hover:bg-[#FAFAFA] cursor-pointer"
                      data-testid={`coverage-row-${key}`}>
                      <td className="pl-3 text-[#9CA3AF]">
                        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-[#1F2937]">{r.career_name}</span>
                        <span className="block text-[11px] text-[#9CA3AF]">
                          {r.kind === 'profession' ? 'Reported by a parent' : r.category}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-[#4B5563]" data-testid={`coverage-mentors-${key}`}>
                        {r.kind === 'profession' ? <span className="text-[#C8CDD6]">—</span>
                          : r.mentor_count || <span className="text-[#C8CDD6]">0</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right text-[#4B5563]" data-testid={`coverage-requests-${key}`}>
                        {r.kind === 'profession' ? <span className="text-[#C8CDD6]">—</span>
                          : r.request_count || <span className="text-[#C8CDD6]">0</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right text-[#4B5563]">
                        {r.kind === 'profession' ? <span className="text-[#C8CDD6]">—</span>
                          : r.completed_count || <span className="text-[#C8CDD6]">0</span>}
                      </td>
                      <td className={`px-4 py-2.5 text-xs whitespace-nowrap ${COVERAGE_TONE[r.coverage_state] || 'text-[#6B7280]'}`}
                        data-testid={`coverage-state-${key}`}>
                        {COVERAGE_LABEL[r.coverage_state] || '—'}
                        {r.coverage_state === 'invite_to_mentor' && (
                          r.invitable_count > 0 ? (
                            <button type="button"
                              onClick={(e) => { e.stopPropagation(); setAskTarget(r); }}
                              className="block mt-0.5 text-[11px] font-semibold text-[#F97D6A] hover:text-[#F06651]"
                              data-testid={`coverage-invite-to-mentor-${key}`}>
                              Invite {r.invitable_count === 1 ? 'the parent' : `${r.invitable_count} parents`}
                            </button>
                          ) : (
                            <span className="block mt-0.5 text-[11px] text-[#9CA3AF]"
                              data-testid={`coverage-invited-${key}`}>
                              {r.asked_count === 1 ? 'Parent already invited' : 'Parents already invited'}
                            </span>
                          )
                        )}
                        {r.kind === 'profession' && (
                          <span className="block text-[11px] text-[#9CA3AF]">
                            {LIBRARY_STATUS[r.library_status] || 'Pending review'}
                          </span>
                        )}
                      </td>
                    </tr>
                    {open && (
                      <tr className="border-b border-[#F3F4F6] bg-[#FCFCFD]">
                        <td />
                        <td colSpan={COLUMNS.length} className="px-4">
                          <CoverageDetails row={r} onInvite={setAskTarget} onNudge={nudge}
                            nudging={nudging} refreshToken={refreshToken} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AskParentsDialog
        target={askTarget}
        onOpenChange={() => setAskTarget(null)}
        onSent={async () => { await load(); setRefreshToken(n => n + 1); }}
      />
    </div>
  );
}
